import React, { ChangeEvent, forwardRef, useCallback, useState } from 'react';

import { clsx } from 'clsx';

import { BaseTextField, BaseTextFieldProps } from './BaseTextField';

export interface MoneyFieldProps
    extends Omit<BaseTextFieldProps, 'type' | 'step' | 'defaultValue' | 'value'> {
    value?: number;
    defaultValue?: number;
    step?: number;
    errorMessage?: string;
    allowDecimals?: boolean;
}

export const MoneyField = forwardRef<HTMLInputElement, MoneyFieldProps>(
    (
        {
            step = 0.01,
            onChange,
            className = '',
            defaultValue,
            value,
            errorMessage = 'This field must be a cash value.',
            allowDecimals = true,
            ...props
        },
        ref,
    ) => {
        const [error, setError] = useState(false);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const isNumeric = !!e.key.match(/[0-9]/g)?.length;
            const isDecimal = e.key === '.';
            const isValidKey = e.key.length > 1; // Accept keys such as 'backspace', 'delete', 'left arrow', 'right arrow'

            // @ts-ignore event.target.value does exist
            const { value: eventValue } = e.target;
            const hasDecimal = eventValue.indexOf('.') > -1;
            const hasMaxDecimalPoints = eventValue.charAt(eventValue.length - 3) === '.';

            // If decimals are not allowed, block decimal point
            if (!allowDecimals && isDecimal) {
                setError(true);
                e.preventDefault();
                return;
            }

            const shouldPrevent = allowDecimals
                ? !(isNumeric || isDecimal || isValidKey) || (hasDecimal && isDecimal)
                : !(isNumeric || isValidKey);

            // For decimal point restrictions, just prevent without showing error
            if (allowDecimals && isNumeric && hasMaxDecimalPoints) {
                e.preventDefault();
                return;
            }

            if (shouldPrevent) {
                setError(true);
                e.preventDefault();
                return;
            }

            setError(false);
        };

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                // Call the standard onChange
                onChange?.(event);
            },
            [onChange],
        );

        // Convert number values to strings for BaseTextField
        const stringValue = value !== undefined ? String(value) : undefined;
        const stringDefaultValue = defaultValue !== undefined ? String(defaultValue) : undefined;

        // Adjust step for whole numbers
        const finalStep = allowDecimals ? step : 1;

        return (
            <BaseTextField
                {...props}
                ref={ref}
                type="number"
                step={finalStep}
                value={stringValue}
                defaultValue={stringDefaultValue}
                className={clsx('money-textfield', className)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                error={props.error || error}
                helperText={error ? errorMessage : props.helperText}
            />
        );
    },
);

MoneyField.displayName = 'MoneyField';
